"use client";

import { useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import type { KundaliRequest } from "@/lib/api/kundali";
import type { PlaceResult } from "@/lib/api/misc";
import { kundaliFormSchema, toKundaliRequest } from "@ishvaram/api-client/forms/kundali";
import Button from "@ishvaram/ui/Button";
import Input from "@ishvaram/ui/Input";
import PlaceAutocomplete from "./PlaceAutocomplete";
import { trackFreeToolStarted } from "@/lib/analytics";

interface BirthDetailsFormProps {
  onSubmit: (data: KundaliRequest) => void | Promise<void>;
  loading: boolean;
  submitLabel?: string;
  error?: string;
  /** Optional label above the form (e.g. "Person A") */
  heading?: string;
  /** When true, marks birth time as explicitly required in the UI (for
   *  ascendant / lagna / muhurta calculators where time is load-bearing). */
  requireBirthTime?: boolean;
  language?: "en" | "hi";
  analyticsToolName?: string;
  analyticsStep?: string;
  analyticsPlacement?: string;
}

type BirthFormLabels = ReturnType<typeof birthFormLabels>;

export default function BirthDetailsForm({
  onSubmit,
  loading,
  submitLabel,
  error,
  heading,
  requireBirthTime = false,
  language = "en",
  analyticsToolName,
  analyticsStep,
  analyticsPlacement = "birth_details_form",
}: BirthDetailsFormProps) {
  const t = birthFormLabels(language);
  const effectiveSubmitLabel = submitLabel ?? t.defaultSubmit;
  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [dateError, setDateError] = useState<string | undefined>();
  const trackedStartRef = useRef(false);

  // Today in YYYY-MM-DD for `max` on date input — no future births.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Geocoding hasn't completed (user typed but didn't pick a suggestion).
  const placeIsUnresolved = birthPlace.trim().length > 0 && selectedPlace === null;

  function validateDate(val: string): string | undefined {
    if (!val) return undefined;
    const year = parseInt(val.slice(0, 4), 10);
    const currentYear = new Date().getFullYear();
    if (isNaN(year) || year < 1900) return t.yearTooEarly;
    if (year > currentYear) return t.futureDate;
    return undefined;
  }

  function handleDateChange(val: string) {
    trackStart();
    setBirthDate(val);
    setDateError(validateDate(val));
  }

  function trackStart() {
    if (!analyticsToolName || trackedStartRef.current) return;
    trackedStartRef.current = true;
    trackFreeToolStarted({
      tool_name: analyticsToolName,
      source_path: typeof window === "undefined" ? undefined : window.location.pathname,
      language,
      placement: analyticsPlacement,
      step: analyticsStep,
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    trackStart();
    const err = validateDate(birthDate);
    if (err) {
      setDateError(err);
      return;
    }
    const parsed = kundaliFormSchema.safeParse({
      name,
      birthDate,
      birthTime: birthTime || null,
      birthPlace,
      ...(selectedPlace && {
        latitude: selectedPlace.latitude,
        longitude: selectedPlace.longitude,
        timezone: selectedPlace.timezone,
      }),
    });
    if (!parsed.success) return;
    onSubmit(toKundaliRequest(parsed.data) as KundaliRequest);
  }

  return (
    <form onSubmit={handleSubmit} className="card-sacred p-6 sm:p-8 space-y-5">
      <FormHeading heading={heading} />
      <NameField
        heading={heading}
        labels={t}
        name={name}
        setName={setName}
        trackStart={trackStart}
      />
      <DateTimeFields
        heading={heading}
        labels={t}
        birthDate={birthDate}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        requireBirthTime={requireBirthTime}
        todayIso={todayIso}
        dateError={dateError}
        handleDateChange={handleDateChange}
        trackStart={trackStart}
      />
      <PlaceField
        heading={heading}
        labels={t}
        birthPlace={birthPlace}
        setBirthPlace={setBirthPlace}
        setSelectedPlace={setSelectedPlace}
        placeIsUnresolved={placeIsUnresolved}
        trackStart={trackStart}
      />
      <FormError error={error} />
      <SubmitButton
        loading={loading}
        calculatingLabel={t.calculating}
        submitLabel={effectiveSubmitLabel}
      />
    </form>
  );
}

function birthFormLabels(language: "en" | "hi") {
  const isHindi = language === "hi";
  return {
    yourName: isHindi ? "आपका नाम" : "Your Name",
    enterFullName: isHindi ? "अपना पूरा नाम दर्ज करें" : "Enter your full name",
    birthDate: isHindi ? "जन्म तिथि" : "Birth Date",
    birthTime: isHindi ? "जन्म समय" : "Birth Time",
    required: isHindi ? "(आवश्यक)" : "(required)",
    optional: isHindi ? "(वैकल्पिक)" : "(optional)",
    timeRequiredHint: isHindi
      ? "लग्न प्रत्येक 2 घंटे में बदलता है — सटीक समय आवश्यक है।"
      : "Ascendant changes every ~2 hours — accurate time is essential.",
    timeOptionalHint: isHindi
      ? "अज्ञात होने पर छोड़ें। लग्न आधारित विश्लेषण के लिए सटीक समय चाहिए।"
      : "Skip if unknown. Lagna-based insights need exact time.",
    birthPlace: isHindi ? "जन्म स्थान" : "Birth Place",
    pickCityHint: isHindi
      ? "सटीक निर्देशांक के लिए सुझावों में से शहर चुनें।"
      : "Pick a city from the suggestions for precise coordinates.",
    calculating: isHindi ? "गणना हो रही है..." : "Calculating...",
    yearTooEarly: isHindi ? "वर्ष 1900 या उसके बाद का होना चाहिए।" : "Year must be 1900 or later.",
    futureDate: isHindi
      ? "जन्म तिथि भविष्य की नहीं हो सकती।"
      : "Birth date cannot be in the future.",
    defaultSubmit: isHindi ? "गणना करें" : "Calculate",
  };
}

function FormHeading({ heading }: { heading?: string }) {
  if (!heading) return null;
  return (
    <h3
      className="text-[16px] font-bold"
      style={{
        fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
        color: "var(--ish-dark)",
      }}
    >
      {heading}
    </h3>
  );
}

function fieldId(name: string, heading?: string): string {
  return `${name}-${heading || "default"}`;
}

function NameField({
  heading,
  labels,
  name,
  setName,
  trackStart,
}: {
  heading?: string;
  labels: BirthFormLabels;
  name: string;
  setName: (value: string) => void;
  trackStart: () => void;
}) {
  return (
    <Input
      id={fieldId("name", heading)}
      type="text"
      required
      label={labels.yourName}
      value={name}
      onChangeText={(value) => {
        trackStart();
        setName(value);
      }}
      placeholder={labels.enterFullName}
      autoComplete="name"
      autoCapitalize="words"
    />
  );
}

function DateTimeFields({
  heading,
  labels,
  birthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  todayIso,
  dateError,
  handleDateChange,
  trackStart,
}: {
  heading?: string;
  labels: BirthFormLabels;
  birthDate: string;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  todayIso: string;
  dateError?: string;
  handleDateChange: (value: string) => void;
  trackStart: () => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Input
        id={fieldId("birthDate", heading)}
        type="date"
        required
        label={labels.birthDate}
        value={birthDate}
        onChangeText={handleDateChange}
        autoComplete="bday"
        min="1900-01-01"
        max={todayIso}
        error={dateError}
      />
      <BirthTimeField
        heading={heading}
        labels={labels}
        birthTime={birthTime}
        setBirthTime={setBirthTime}
        requireBirthTime={requireBirthTime}
        trackStart={trackStart}
      />
    </div>
  );
}

function BirthTimeField({
  heading,
  labels,
  birthTime,
  setBirthTime,
  requireBirthTime,
  trackStart,
}: {
  heading?: string;
  labels: BirthFormLabels;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  trackStart: () => void;
}) {
  return (
    <div>
      <label
        htmlFor={fieldId("birthTime", heading)}
        className="block text-[13px] font-semibold mb-2"
        style={{ color: "var(--ish-dark)" }}
      >
        {labels.birthTime}{" "}
        <span className="font-normal" style={{ color: "var(--ish-text-faint)" }}>
          {requireBirthTime ? labels.required : labels.optional}
        </span>
      </label>
      <Input
        id={fieldId("birthTime", heading)}
        type="time"
        required={requireBirthTime}
        value={birthTime}
        onChangeText={(value) => {
          trackStart();
          setBirthTime(value);
        }}
      />
      <p className="mt-1.5 text-[11px] leading-snug" style={{ color: "var(--ish-text-faint)" }}>
        {requireBirthTime ? labels.timeRequiredHint : labels.timeOptionalHint}
      </p>
    </div>
  );
}

function PlaceField({
  heading,
  labels,
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  placeIsUnresolved,
  trackStart,
}: {
  heading?: string;
  labels: BirthFormLabels;
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  placeIsUnresolved: boolean;
  trackStart: () => void;
}) {
  return (
    <div>
      <label
        htmlFor={fieldId("birthPlace", heading)}
        className="block text-[13px] font-semibold mb-2"
        style={{ color: "var(--ish-dark)" }}
      >
        {labels.birthPlace}
      </label>
      <PlaceAutocomplete
        id={fieldId("birthPlace", heading)}
        value={birthPlace}
        onChange={(value) => {
          trackStart();
          setBirthPlace(value);
          setSelectedPlace(null);
        }}
        onSelect={(place) => {
          trackStart();
          setBirthPlace(place.display);
          setSelectedPlace(place);
        }}
      />
      {placeIsUnresolved && (
        <p className="mt-1.5 text-[11px] leading-snug" style={{ color: "var(--ish-text-faint)" }}>
          {labels.pickCityHint}
        </p>
      )}
    </div>
  );
}

function FormError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="text-[13px] rounded-lg p-3.5"
      style={{
        color: "var(--color-error-text)",
        backgroundColor: "var(--color-error-bg)",
        border: "1px solid var(--color-error-tint)",
      }}
    >
      {error}
    </div>
  );
}

function SubmitButton({
  loading,
  calculatingLabel,
  submitLabel,
}: {
  loading: boolean;
  calculatingLabel: string;
  submitLabel: string;
}) {
  return (
    <Button
      type="submit"
      onPress={() => {
        /* form.onSubmit handles it — this keeps Enter-key behavior */
      }}
      disabled={loading}
      loading={loading}
      variant="primary"
      title={loading ? calculatingLabel : `${submitLabel} \u2192`}
      className="w-full py-3.5"
    />
  );
}
